import type { ScheduledPostDto } from '@reelplicate/api-client/model';
import {
  getListScheduledPostsQueryKey,
  useEnterPostMetrics,
} from '@reelplicate/api-client/scheduled-posts';
import { Button, Field, Inline, Stack, TextInput, toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { formatCount } from '@/features/analytics/format';
import { formatWhen } from '@/lib/format-when';

import { API_PLATFORM_NAMES } from './calendar-model';

const FIELDS = [
  { key: 'views', label: 'Views', step: '1' },
  { key: 'avgWatchSeconds', label: 'Avg watch (s)', step: '0.1' },
  { key: 'saves', label: 'Saves', step: '1' },
  { key: 'shares', label: 'Shares', step: '1' },
] as const;
type Key = (typeof FIELDS)[number]['key'];

const TOTALS = [
  { key: 'views', label: 'Views' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'shares', label: 'Shares' },
  { key: 'saves', label: 'Saves' },
] as const;
type TotalKey = (typeof TOTALS)[number]['key'];

/** Each count summed over the accounts that report it; null when none does. */
function platformTotals(post: ScheduledPostDto) {
  const measured = post.accounts.filter((a) => a.metrics);
  const totals = Object.fromEntries(
    TOTALS.map(({ key }) => {
      const values = measured
        .map((a) => a.metrics![key])
        .filter((v): v is number => v !== null);
      return [key, values.length ? values.reduce((sum, v) => sum + v, 0) : null];
    }),
  ) as Record<TotalKey, number | null>;
  const measuredAt = measured
    .map((a) => a.metrics!.measuredAt)
    .sort()
    .at(-1);
  const sources = [...new Set(measured.map((a) => API_PLATFORM_NAMES[a.platform]))];
  // Counts some measured account doesn't report: worth entering by hand.
  const gaps = measured.flatMap((a) =>
    (['saves', 'shares'] as const)
      .filter((key) => a.metrics![key] === null)
      .map((key) => `${key} on ${API_PLATFORM_NAMES[a.platform]}`),
  );
  return { measured: measured.length > 0, totals, measuredAt, sources, gaps };
}

/**
 * How a post did. The platforms' own numbers lead (summed over its accounts;
 * each account's are on its row). Experiments' Pick winner reads the numbers
 * entered here, which cover what the platforms don't report - avg watch time,
 * and saves or shares where a platform keeps them to itself.
 */
export function PostResults({
  workspaceId,
  post,
}: {
  workspaceId: string;
  post: ScheduledPostDto;
}) {
  const platform = platformTotals(post);
  const [entering, setEntering] = useState(!platform.measured || post.metrics !== null);
  return (
    <Stack as="section" aria-label="Results" gap={3}>
      <h3 className="text-label-md text-text-primary">Results</h3>
      {platform.measured && (
        <Stack gap={1}>
          <dl
            aria-label="From the platforms"
            className="tablet:grid-cols-5 grid grid-cols-3 gap-2"
          >
            {TOTALS.map(({ key, label }) => (
              <div key={key}>
                <dt className="text-caption text-text-tertiary">{label}</dt>
                <dd className="text-heading-h4 text-text-primary tabular-nums">
                  {platform.totals[key] === null
                    ? '—'
                    : formatCount(platform.totals[key])}
                </dd>
              </div>
            ))}
          </dl>
          <span className="text-caption text-text-tertiary">
            From {platform.sources.join(', ')}
            {platform.measuredAt && ` · ${formatWhen(platform.measuredAt)}`}
          </span>
        </Stack>
      )}
      {entering ? (
        <ManualResults
          workspaceId={workspaceId}
          post={post}
          platform={platform}
          onCancel={platform.measured ? () => setEntering(false) : undefined}
        />
      ) : (
        <Inline gap={2} align="between">
          <span className="text-caption text-text-secondary">
            Platforms don’t report avg watch time
            {platform.gaps.length > 0 && ` or ${platform.gaps.join(', ')}`}.
          </span>
          <Button size="sm" variant="outline" onClick={() => setEntering(true)}>
            Enter by hand
          </Button>
        </Inline>
      )}
    </Stack>
  );
}

/** The numbers typed in; prefilled from the platforms where they report them. */
function ManualResults({
  workspaceId,
  post,
  platform,
  onCancel,
}: {
  workspaceId: string;
  post: ScheduledPostDto;
  platform: ReturnType<typeof platformTotals>;
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();
  const enter = useEnterPostMetrics();
  const [values, setValues] = useState<Record<Key, string>>(() => {
    const manual = post.metrics;
    const from = (key: 'views' | 'saves' | 'shares') =>
      String(manual?.[key] ?? platform.totals[key] ?? '');
    return {
      views: from('views'),
      avgWatchSeconds: String(manual?.avgWatchSeconds ?? ''),
      saves: from('saves'),
      shares: from('shares'),
    };
  });
  const complete = FIELDS.every(
    ({ key }) => values[key] !== '' && Number(values[key]) >= 0,
  );

  const save = async () => {
    try {
      await enter.mutateAsync({
        id: workspaceId,
        postId: post.id,
        data: {
          views: Math.round(Number(values.views)),
          avgWatchSeconds: Number(values.avgWatchSeconds),
          saves: Math.round(Number(values.saves)),
          shares: Math.round(Number(values.shares)),
        },
      });
    } catch {
      return; // Shown as a toast.
    }
    await queryClient.invalidateQueries({
      queryKey: getListScheduledPostsQueryKey(workspaceId),
    });
    toast.success('Results saved');
  };

  const source = post.metrics
    ? post.metrics.source === 'MANUAL'
      ? `Entered ${formatWhen(post.metrics.measuredAt)}`
      : `${post.metrics.source === 'SIMULATED' ? 'Simulated' : 'Recorded'} · ${formatWhen(post.metrics.measuredAt)}`
    : 'Experiments pick winners from these';
  return (
    <Stack as="section" aria-label="Entered by hand" gap={3}>
      <Inline gap={2} align="between">
        <h4 className="text-label-sm text-text-secondary">
          {platform.measured ? 'Entered by hand, for experiments' : 'Entered by hand'}
        </h4>
        <span className="text-caption text-text-secondary">{source}</span>
      </Inline>
      <div className="tablet:grid-cols-4 grid grid-cols-2 gap-3">
        {FIELDS.map(({ key, label, step }) => (
          <Field key={key} label={label}>
            <TextInput
              size="sm"
              type="number"
              min="0"
              step={step}
              inputMode="decimal"
              value={values[key]}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, [key]: event.target.value }))
              }
            />
          </Field>
        ))}
      </div>
      <Inline gap={2}>
        <Button
          size="sm"
          variant="outline"
          disabled={!complete}
          loading={enter.isPending}
          onClick={() => void save()}
        >
          Save results
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Inline>
    </Stack>
  );
}
